"use client";

import Link from "@/components/ui/intent-link";
import { useSyncExternalStore } from "react";

import { useConsent } from "@/lib/consent-context";
import { useIsEmbedded } from "@/lib/embedded";

const EMPTY_SUBSCRIBE = () => () => {};

function useHasMounted(): boolean {
  return useSyncExternalStore(
    EMPTY_SUBSCRIBE,
    () => true,
    () => false,
  );
}

export function ConsentBanner() {
  const { hasDecided, acceptAll, rejectAll, openSettings } = useConsent();
  const isEmbedded = useIsEmbedded();
  const hasMounted = useHasMounted();

  if (!hasMounted) return null;
  if (hasDecided || isEmbedded) return null;

  return (
    <div
      role="dialog"
      aria-modal="false"
      aria-label="Cookie consent"
      className="fixed inset-x-0 bottom-0 z-50 px-3 pb-3 md:bottom-6 md:px-6 md:pb-0"
    >
      <div className="bg-navy-03 mx-auto flex max-w-140 flex-col gap-5 rounded-[20px] px-5 py-5 text-white shadow-[0_20px_48px_-12px_rgba(0,0,0,0.45)] ring-1 ring-white/15 md:gap-6 md:px-7 md:py-6">
        <div className="flex max-w-[460px] flex-col gap-2">
          <h2 className="text-base leading-tight font-bold tracking-[-0.01em] md:text-lg">
            Your choices regarding cookies on this site
          </h2>
          <p className="text-sm leading-relaxed text-white/85">
            We and our advertising partners use cookies and similar technologies to run the site,
            measure performance, and personalise ads — this can involve collecting and using your
            personal data for ads personalisation. Learn more in our{" "}
            <Link href="/cookie-policy" className="hover:text-teal-04 underline underline-offset-2">
              Cookie Policy
            </Link>{" "}
            and about{" "}
            <a
              href="https://business.safety.google/privacy/"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-teal-04 underline underline-offset-2"
            >
              how Google uses data
            </a>
            .
          </p>
        </div>

        <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:gap-3">
          <button
            type="button"
            onClick={acceptAll}
            className="text-navy-03 hover:bg-teal-03 inline-flex h-11 cursor-pointer items-center justify-center rounded-lg bg-white px-5 text-sm font-bold transition-colors"
          >
            Allow All
          </button>
          <button
            type="button"
            onClick={rejectAll}
            className="inline-flex h-11 cursor-pointer items-center justify-center rounded-lg border border-white/40 px-5 text-sm font-bold text-white transition-colors hover:bg-white/10"
          >
            Use necessary cookies only
          </button>
          <button
            type="button"
            onClick={openSettings}
            className="mt-3 inline-flex cursor-pointer items-center justify-center text-sm leading-[1.054] font-bold text-white underline-offset-4 hover:underline sm:mt-0 sm:ml-auto sm:justify-start"
          >
            Manage Cookies
          </button>
        </div>
      </div>
    </div>
  );
}
